import { useState } from 'react'
import { motion } from 'framer-motion'
import { Download01Icon as Download, ComputerIcon as Monitor, TestTube01Icon as FlaskConical, Shield01Icon as Shield, FlashIcon as Zap, Film01Icon as Film, CropIcon as Crop, Cursor01Icon as MousePointer } from 'hugeicons-react'

// ── Download URL (can be set via VITE_DOWNLOAD_URL or Cloudflare R2 URL) ──────────
const DOWNLOAD_URL = import.meta.env.VITE_DOWNLOAD_URL || 'https://download.screenrecord.ing/screenrecord.ing.Setup.0.1.0.exe'
const VERSION = '0.1.0-alpha'
const RELEASE_DATE = 'July 2026'

declare function gtag(...args: any[]): void

function trackDownload() {
    try {
        gtag('event', 'download', {
            event_category: 'alpha',
            event_label: `windows-${VERSION}`,
        })
    } catch {}
}

const features = [
    { icon: Monitor, label: 'Full screen or area recording' },
    { icon: Crop, label: 'Drag-to-select any region' },
    { icon: MousePointer, label: 'Animated cursor overlay' },
    { icon: Film, label: 'Background, padding & effects' },
    { icon: Zap, label: 'Export MP4 / GIF / WebM' },
    { icon: Shield, label: 'All processing stays on-device' },
]

export default function AlphaPage() {
    const [clicked, setClicked] = useState(false)

    const handleDownload = () => {
        trackDownload()
        setClicked(true)
        window.location.href = DOWNLOAD_URL
    }

    return (
        <div className="min-h-screen bg-[#0a0a0c] text-white flex flex-col items-center justify-center px-4 py-20">

            {/* Badge */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8 flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-500/10 border border-brand-500/20 text-brand-400 text-sm font-medium"
            >
                <FlaskConical size={14} />
                Alpha — Windows only · {VERSION}
            </motion.div>

            {/* Heading */}
            <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="text-5xl md:text-6xl font-bold text-center leading-tight mb-4"
            >
                screenrecord<span className="text-brand-400">.ing</span>
            </motion.h1>

            <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="text-lg text-white/50 text-center max-w-md mb-12"
            >
                Beautiful screen recordings with a built-in editor. Record, trim, add effects and export — no account needed.
            </motion.p>

            {/* Download button */}
            <motion.button
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.15 }}
                onClick={handleDownload}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="group relative flex items-center gap-3 px-8 py-5 rounded-2xl bg-brand-500 hover:bg-brand-400 text-white font-semibold text-lg shadow-xl shadow-brand-500/25 transition-colors duration-200 mb-4"
            >
                <div className="absolute inset-0 rounded-2xl bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                <Download size={22} className={clicked ? 'animate-bounce' : ''} />
                {clicked ? 'Downloading…' : 'Download for Windows'}
            </motion.button>

            <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-sm text-white/30 mb-16"
            >
                Windows 10/11 · 64-bit · {RELEASE_DATE}
            </motion.p>

            {/* Windows SmartScreen note */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.25 }}
                className="mb-16 max-w-sm text-center text-xs text-white/25 leading-relaxed"
            >
                <Shield size={12} className="inline mr-1 mb-0.5" />
                Windows may show a SmartScreen warning for unsigned apps.
                Click <span className="text-white/40 font-medium">More info → Run anyway</span> to proceed.
            </motion.div>

            {/* Features */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-xl w-full"
            >
                {features.map(({ icon: Icon, label }) => (
                    <div
                        key={label}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.06] text-sm text-white/60"
                    >
                        <Icon size={16} className="text-brand-400 shrink-0" />
                        {label}
                    </div>
                ))}
            </motion.div>

            {/* Footer */}
            <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="mt-16 text-xs text-white/20"
            >
                This is an alpha build. Bugs are expected — feedback welcome.
            </motion.p>
        </div>
    )
}
